import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, EASING } from "@/components/Reveal";
import { usePageTitle } from "@/components/PageHero";
import CTABand from "@/components/CTABand";

function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);

  return (
    <section data-testid="hero" className="relative min-h-[100svh] bg-oxford flex flex-col overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0 scale-[1.12]">
        <motion.img
          src="/brand/ref-towers.webp"
          alt="Strategic procurement consulting — arquitectura corporativa"
          className="w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: EASING }}
        />
        <div className="absolute inset-0 bg-oxford/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-oxford via-transparent to-oxford/60" />
      </motion.div>

      <div className="absolute right-[-10%] top-[20%] w-[55%] h-px bg-gradient-to-l from-gold/50 to-transparent -rotate-[18deg] origin-right pointer-events-none" />
      <img src="/brand/isotipo-gold.webp" alt="" className="absolute -left-24 bottom-10 w-[380px] opacity-[0.05] pointer-events-none" />

      <div className="relative flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-24">
        <motion.div
          className="flex items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 2.1 }}
        >
          <span className="h-px w-12 bg-gold" />
          <span className="eyebrow">Compras que impulsan tu negocio</span>
        </motion.div>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
          <MaskedLine delay={2.15} immediate className="text-white">Better Buying.</MaskedLine>
          <MaskedLine delay={2.3} immediate className="text-gold">Better Business.</MaskedLine>
        </h1>

        <motion.p
          className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-steel"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.55, ease: EASING }}
        >
          Construimos capacidades de Compras sólidas y estratégicas que generan valor
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.7, ease: EASING }}
        >
          <Link to="/nosotros" data-testid="hero-cta-secondary" className="btn-outline-light">
            Conoce BRIK
          </Link>
          <Link to="/contacto" data-testid="hero-cta-primary" className="btn-gold">
            Solicita tu diagnóstico <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </motion.div>
      </div>

      <motion.div
        className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pb-8 flex items-end justify-between"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 3 }}
      >
        <p className="font-display text-xs sm:text-sm font-semibold tracking-[0.35em] uppercase text-steel">
          Procurement <span className="text-gold mx-2">—</span> Strategy <span className="text-gold mx-2">—</span> Execution
        </p>
        <div className="hidden sm:flex flex-col items-center gap-3" data-testid="scroll-indicator">
          <span className="font-display text-[9px] font-semibold tracking-[0.35em] uppercase text-steel">Scroll</span>
          <div className="relative h-14 w-px bg-white/15 overflow-hidden">
            <motion.span
              className="absolute top-0 left-0 w-full h-1/2 bg-gold"
              animate={{ y: ["-100%", "200%"] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

const FLOW_WORDS = ["Estrategia", "Ejecución", "Inteligencia", "Valor"];

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="relative bg-white overflow-hidden">
      <img src="/brand/isotipo-gold.webp" alt="" className="absolute -right-28 top-1/2 -translate-y-1/2 w-[480px] opacity-[0.04] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28 lg:py-40">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-gold" />
            <span className="eyebrow">Manifiesto BRIK</span>
          </div>
        </Reveal>
        <h2 className="mt-10 max-w-5xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.12] text-oxford">
          <MaskedLine>Compras no es solamente</MaskedLine>
          <MaskedLine delay={0.12}>una función.</MaskedLine>
          <MaskedLine delay={0.24} className="text-gold">Es una ventaja competitiva.</MaskedLine>
        </h2>

        <div className="mt-20 lg:mt-28 border-t border-oxford/10 pt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          {FLOW_WORDS.map((word, i) => (
            <Reveal key={word} delay={i * 0.12}>
              <div className="group flex items-center gap-4" data-testid={`manifesto-pillar-${i + 1}`}>
                <span className="font-display text-sm font-semibold text-gold">0{i + 1}</span>
                <span className="font-display text-base lg:text-xl font-semibold uppercase tracking-[0.15em] text-oxford">{word}</span>
                {i < FLOW_WORDS.length - 1 && (
                  <ArrowRight className="hidden lg:block h-4 w-4 text-gold/50 shrink-0" strokeWidth={1.5} />
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.4}>
          <div className="mt-20 lg:mt-24 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
            <p className="font-display text-base lg:text-lg font-semibold text-oxford">
              Somos tu aliado estratégico en Procurement.
            </p>
            <span className="hidden sm:block h-px w-16 bg-gold" />
            <Link
              to="/nosotros"
              data-testid="manifesto-link"
              className="group inline-flex items-center gap-2 font-display text-[11px] font-semibold tracking-[0.22em] uppercase text-oxford/70 transition-colors duration-300 hover:text-gold w-fit"
            >
              Conoce BRIK <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function People() {
  return (
    <section data-testid="people-section" className="relative bg-oxford diagonal-lines overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="h-px w-10 bg-gold" />
                <span className="eyebrow">Nuestra filosofía</span>
              </div>
            </Reveal>
            <h2 className="mt-10 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">
              <MaskedLine delay={0.1} className="text-white">Better Buying.</MaskedLine>
              <MaskedLine delay={0.25} className="text-gold">Better Business.</MaskedLine>
            </h2>
            <Reveal delay={0.35}>
              <p className="mt-10 max-w-lg text-sm lg:text-base leading-relaxed text-steel">
                Una función de Procurement sólida debe generar valor financiero, desarrollar relaciones estratégicas con proveedores, reducir riesgos, mejorar procesos y proporcionar información que permita tomar mejores decisiones.
              </p>
            </Reveal>
            <Reveal delay={0.45}>
              <p className="mt-5 max-w-lg text-sm lg:text-base leading-relaxed text-steel">
                Por eso nuestra filosofía parte de construir capacidades de manera integral:
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <p className="mt-6 max-w-lg font-display text-sm lg:text-base font-semibold tracking-wide leading-loose text-white">
                Estrategia <span className="text-gold">+</span> Procesos <span className="text-gold">+</span> Proveedores <span className="text-gold">+</span> Negociación <span className="text-gold">+</span> Inteligencia <span className="text-gold">+</span> Talento <span className="text-gold">+</span> Ejecución
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <p className="mt-9 max-w-lg border-l-2 border-gold pl-5 text-sm lg:text-base font-medium leading-relaxed text-white/90">
                Cada elemento es un BRIK que fortalece la capacidad de compra de una organización.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <div className="relative">
              <span className="absolute -bottom-4 -left-4 w-full h-full border border-gold/40 pointer-events-none" />
              <img
                src="/brand/brand-team.webp"
                alt="Equipo BRIK Procurement Partners — Valeria Longinos, Founder y Directora General, con su equipo"
                loading="lazy"
                className="relative w-full aspect-[4/3] object-cover object-top"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  usePageTitle("BRIK Procurement Partners | Strategic Procurement & Purchasing");
  return (
    <>
      <Hero />
      <Manifesto />
      <People />
      <CTABand image="/brand/ref-hq.webp" text="Construyamos una función de Compras más estratégica, eficiente y capaz de generar valor sostenible para tu negocio." />
    </>
  );
}
